<!-- src/components/dashboard/WidgetContainer.vue -->
<template>
  <div 
    class="widget-container" 
    :class="{ 
      'is-locked': dashboardStore.isLocked, 
      'is-mobile': isMobile,
      'is-offline': !natsStore.isConnected
    }"
  >
    <div v-if="!config" class="widget-error">
      <div class="error-icon">⚠️</div>
      <div class="error-message">Configuration missing</div>
    </div>
    
    <template v-else>
      <!-- HEADER LOGIC -->
      <div 
        v-if="shouldShowHeader" 
        class="widget-header vue-draggable-handle"
      >
        <div v-if="!isMobile" class="widget-title" :title="config.title">{{ config.title }}</div>
        <div v-else class="widget-title mobile-spacer"></div>

        <div v-if="!natsStore.isConnected" class="offline-indicator" title="Disconnected from NATS">
          ⚠️
        </div>

        <div class="widget-actions">
          <button class="icon-btn" title="Full Screen" @click="$emit('fullscreen')">⛶</button>
          
          <template v-if="!dashboardStore.isLocked">
            <button class="icon-btn" title="Duplicate" @click="$emit('duplicate')">📋</button>
            <button class="icon-btn" title="Configure" @click="$emit('configure')">⚙️</button>
            <button class="icon-btn danger" title="Delete" @click="handleDelete">✕</button>
          </template>
        </div>
      </div>
      
      <div class="widget-body">
        <div v-if="error" class="widget-error">
          <div class="error-icon">⚠️</div>
          <div class="error-message">{{ error }}</div>
        </div>
        
        <component 
          v-else
          :is="widgetComponent" 
          :config="config"
          :layout-mode="isMobile ? 'card' : 'standard'"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onErrorCaptured } from 'vue'
import { useDashboardStore } from '@/stores/dashboard'
import { useNatsStore } from '@/stores/nats'
import type { WidgetConfig } from '@/types/dashboard'
import TextWidget from '@/components/widgets/TextWidget.vue'
import ChartWidget from '@/components/widgets/ChartWidget.vue'
import ButtonWidget from '@/components/widgets/ButtonWidget.vue'
import KvWidget from '@/components/widgets/KvWidget.vue'
import SwitchWidget from '@/components/widgets/SwitchWidget.vue'
import SliderWidget from '@/components/widgets/SliderWidget.vue'
import StatCardWidget from '@/components/widgets/StatCardWidget.vue'
import GaugeWidget from '@/components/widgets/GaugeWidget.vue'
import MapWidget from '@/components/widgets/MapWidget.vue'
import ConsoleWidget from '@/components/widgets/ConsoleWidget.vue'
import PublisherWidget from '@/components/widgets/PublisherWidget.vue'
import StatusWidget from '@/components/widgets/StatusWidget.vue'
import MarkdownWidget from '@/components/widgets/MarkdownWidget.vue'

const props = defineProps<{
  config?: WidgetConfig
  isMobile?: boolean
}>()

const emit = defineEmits<{
  delete: []
  configure: []
  duplicate: []
  fullscreen: []
}>()

const dashboardStore = useDashboardStore()
const natsStore = useNatsStore()
const error = ref<string | null>(null)

const widgetComponent = computed(() => {
  if (!props.config) return null
  switch (props.config.type) {
    case 'text': return TextWidget
    case 'chart': return ChartWidget
    case 'button': return ButtonWidget
    case 'kv': return KvWidget
    case 'switch': return SwitchWidget
    case 'slider': return SliderWidget
    case 'stat': return StatCardWidget
    case 'gauge': return GaugeWidget
    case 'map': return MapWidget
    case 'console': return ConsoleWidget
    case 'publisher': return PublisherWidget
    case 'status': return StatusWidget
    case 'markdown': return MarkdownWidget
    default: return null
  }
})

const shouldShowHeader = computed(() => {
  if (!dashboardStore.isLocked) return true
  // Markdown widget might want header hidden even in view mode if title is empty? 
  // Grug say: Keep standard behavior for consistency.
  if (props.isMobile) return false
  return true
})

function handleDelete() {
  if (!props.config) return
  if (confirm(`Delete widget "${props.config.title}"?`)) {
    emit('delete')
  }
}

onErrorCaptured((err) => {
  console.error(`Widget error (${props.config?.id}):`, err)
  error.value = err.message || 'Widget error'
  return false
})
</script>

<style scoped>
/* Same styles as before */
.widget-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--panel);
  border: 1px solid var(--widget-border);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.widget-container:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.widget-container.is-locked {
  border-color: transparent;
  background: var(--widget-bg);
}

.widget-container.is-offline {
  border-color: var(--color-warning);
}

.widget-container.is-offline .widget-body {
  opacity: 0.7;
  filter: grayscale(0.8);
}

.offline-indicator {
  font-size: 14px;
  margin-right: 4px;
  animation: pulse 2s infinite;
  cursor: help;
  user-select: none;
}

@keyframes pulse {
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
}

.widget-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: var(--widget-header-bg);
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
  gap: 8px;
  height: 40px;
}

.widget-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.widget-title.mobile-spacer {
  flex: 1;
}

.widget-actions {
  display: flex;
  gap: 4px;
}

.icon-btn {
  background: transparent;
  border: none;
  color: var(--muted);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  font-size: 14px;
  min-width: 28px;
  min-height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.icon-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text);
}

.icon-btn.danger:hover {
  background: var(--color-error-bg);
  color: var(--color-error);
}

.widget-body {
  flex: 1;
  min-height: 0;
  position: relative;
  container-type: size;
  display: flex;
  flex-direction: column;
}

.widget-body > * {
  flex: 1;
}

.widget-error {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px;
  color: var(--color-error);
  text-align: center;
}

.error-icon { font-size: 24px; margin-bottom: 4px; }
.error-message { font-size: 12px; line-height: 1.4; }
</style>
